import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import { isEmailVerificationRequired, signInWithPassword } from '@/lib/supabase';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

const baseRegisterSchema = {
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  companyName: z.string().min(2, 'Company name is required'),
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      PASSWORD_REGEX,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmPassword: z.string(),
};

// Schema for users who aren't coming from an invitation
const registerSchema = z
  .object(baseRegisterSchema)
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

// Schema for users who are accepting an invitation
const inviteRegisterSchema = z
  .object({
    ...baseRegisterSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;
type InviteRegisterFormValues = z.infer<typeof inviteRegisterSchema>;

export function RegisterForm() {
  const { signUp, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get('email');
  const redirectPath = searchParams.get('redirect');
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormValues | InviteRegisterFormValues>({
    resolver: zodResolver(invitedEmail ? inviteRegisterSchema : registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      email: invitedEmail || '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password');

  const onSubmit = async (data: RegisterFormValues | InviteRegisterFormValues) => {
    setFormError(null);
    setLoading(true);
    
    // Defensive check: ensure companyName is not a fallback or empty
    const fallbackPattern = new RegExp(`^${data.firstName}\s+${data.lastName}'s Organization$`, 'i');
    if (!data.companyName || fallbackPattern.test(data.companyName.trim())) {
      setFormError('Please enter your actual company or team name.');
      setLoading(false);
      return;
    }
    
    try {
      // Step 1: Register the user with metadata
      const { error: signUpError } = await signUp(
        data.email, 
        data.password, 
        {
          // Store minimal data in auth metadata
          name: `${data.firstName} ${data.lastName}`.trim(),
          first_name: data.firstName,
          last_name: data.lastName,
          company_name: data.companyName
        }
      );
      
      if (signUpError) {
        // Error handled by the catch block below
        throw new Error(signUpError.message);
      }
      
      // Show success message
      toast.success(
        isEmailVerificationRequired()
          ? 'Registration successful! Please check your email to verify your account.'
          : 'Registration successful! You are now logged in.'
      );
      
      if (isEmailVerificationRequired()) {
        // Redirect to the invitation link if available, otherwise go to verify email page
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate('/auth/verify-email');
        }
      } else {
        // Log in the user immediately
        const { error: loginError } = await signInWithPassword(data.email, data.password);
        if (loginError) {
          throw new Error(loginError.message || 'Login failed after registration.');
        }
        // Redirect to the invitation link if available, otherwise go to home
        if (redirectPath) {
          navigate(redirectPath);
        } else {
          navigate('/');
        }
      }
    } catch (error: any) {
      setFormError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center">Create an Account</CardTitle>
        <CardDescription className="text-center">
          {invitedEmail 
            ? 'Fill in your details to accept the invitation' 
            : 'Sign up to get started'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {formError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="First name"
                autoComplete="given-name"
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Last name"
                autoComplete="family-name"
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              type="text"
              placeholder="Your company or team name"
              autoComplete="organization"
              {...register('companyName')}
            />
            {errors.companyName && (
              <p className="text-sm text-destructive">{errors.companyName.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              autoComplete="email"
              {...register('email')}
              disabled={!!invitedEmail} // Disable if coming from invitation
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              autoComplete="new-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
            <PasswordStrengthIndicator password={password} />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              autoComplete="new-password"
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>
          
          <Button type="submit" className="w-full" disabled={loading || authLoading}>
            {(loading || authLoading) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create Account
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <Button
            variant="link"
            className="p-0 h-auto"
            onClick={() => navigate('/auth/login')}
          >
            Sign in
          </Button>
        </p>
      </CardFooter>
    </Card>
  );
}